import { Injectable } from '@angular/core';

import { LocalService } from './storage/local.service';

/**
 * User preference keys for localStorage
 * Add new preference keys here as features require sticky settings
 */
export const USER_PREFERENCE_KEYS = {
    SHOW_TX_BANK_CARDS: 'showTxBankCards',
    BUDGET_COMPACT_VIEW: 'budgetCompactView',
    BUDGET_YEAR_TYPE: 'budgetYearType',
    HOMEWISE_MIGRATION_VIEW_MODE: 'homewiseMigrationViewMode',
    // Gable AI drawer — width (px) when in drawer mode, and current mode
    // (drawer vs expanded dialog). Both persist across navigation + sessions.
    GABLE_DRAWER_WIDTH: 'gableDrawerWidth',
    GABLE_DRAWER_MODE: 'gableDrawerMode',
    // Add more preference keys as needed
    // Example: SHOW_ARCHIVED_ITEMS: 'showArchivedItems',
} as const;

/**
 * Keys for dismissible alerts/hints that users can permanently hide
 * These are stored separately with a 'dismissed_' prefix
 */
export const DISMISSIBLE_KEYS = {
    DRAFT_BUDGET_EDIT_HINT: 'draftBudgetEditHint',
    DRAFT_BUDGET_ACTUALS_HINT: 'draftBudgetActualsHint',
    // Banner dismissible keys
    BANNER_PROMO: 'bannerPromo',
    BANNER_1099: 'bannerReminder1099',
    BANNER_CPA_TAX: 'bannerCpaTax',
    BANNER_INTERCOM_NEWS: 'bannerIntercomNews',
    BANNER_RESALE_IN_PROGRESS: 'bannerResaleInProgress',
    // Add more dismissible keys as needed
} as const;

export type DismissibleKey = typeof DISMISSIBLE_KEYS[keyof typeof DISMISSIBLE_KEYS];

type PreferenceKey = typeof USER_PREFERENCE_KEYS[keyof typeof USER_PREFERENCE_KEYS];

@Injectable({
    providedIn: 'root',
})
export class UserPreferencesService {
    private readonly STORAGE_PREFIX = 'userPref_';
    private readonly DISMISSED_PREFIX = 'dismissed_';

    /**
     * Get a user preference from localStorage
     * @param key The preference key from USER_PREFERENCE_KEYS
     * @param defaultValue The default value if preference is not set
     * @returns The stored preference value or the default
     */
    public getPreference<T>(key: PreferenceKey, defaultValue: T): T {
        const storageKey = this.getStorageKey(key);
        const stored = LocalService.get(storageKey);

        if (stored === null) {
            return defaultValue;
        }

        try {
            return JSON.parse(stored) as T;
        } catch (e) {
            console.warn(`Failed to parse preference ${key}, returning default`, e);
            return defaultValue;
        }
    }

    /**
     * Set a user preference in localStorage
     * @param key The preference key from USER_PREFERENCE_KEYS
     * @param value The value to store
     */
    public setPreference<T>(key: PreferenceKey, value: T): void {
        const storageKey = this.getStorageKey(key);
        try {
            LocalService.set(storageKey, JSON.stringify(value));
        } catch (e) {
            console.error(`Failed to save preference ${key}`, e);
        }
    }

    /**
     * Remove a user preference from localStorage
     * @param key The preference key from USER_PREFERENCE_KEYS
     */
    public removePreference(key: PreferenceKey): void {
        const storageKey = this.getStorageKey(key);
        LocalService.destroy(storageKey);
    }

    /**
     * Clear all user preferences
     */
    public clearAllPreferences(): void {
        Object.values(USER_PREFERENCE_KEYS).forEach(key => {
            this.removePreference(key);
        });
    }

    /**
     * Get the full storage key with prefix
     */
    private getStorageKey(key: PreferenceKey): string {
        return `${this.STORAGE_PREFIX}${key}`;
    }

    // ========================================================================
    // DISMISSIBLE ALERTS/HINTS
    // ========================================================================

    /**
     * Check if a dismissible item has been dismissed
     * @param key The dismissible key from DISMISSIBLE_KEYS
     * @returns true if the item has been dismissed
     */
    public isDismissed(key: DismissibleKey): boolean {
        const storageKey = `${this.DISMISSED_PREFIX}${key}`;
        return LocalService.get(storageKey) === 'true';
    }

    /**
     * Mark a dismissible item as dismissed
     * @param key The dismissible key from DISMISSIBLE_KEYS
     */
    public setDismissed(key: DismissibleKey): void {
        const storageKey = `${this.DISMISSED_PREFIX}${key}`;
        LocalService.set(storageKey, 'true');
    }

    /**
     * Reset a dismissible item (show it again)
     * @param key The dismissible key from DISMISSIBLE_KEYS
     */
    public resetDismissed(key: DismissibleKey): void {
        const storageKey = `${this.DISMISSED_PREFIX}${key}`;
        LocalService.destroy(storageKey);
    }

    // ========================================================================
    // DYNAMIC DISMISSIBLE KEYS (for org-scoped or context-specific dismissals)
    // ========================================================================

    /**
     * Check if a dynamic dismissible item has been dismissed
     * Use this for context-specific keys like org-scoped dismissals
     * @param key A dynamic key string (e.g., 'resaleDocsWelcome_12345')
     */
    public isDismissedDynamic(key: string): boolean {
        const storageKey = `${this.DISMISSED_PREFIX}${key}`;
        return LocalService.get(storageKey) === 'true';
    }

    /**
     * Mark a dynamic dismissible item as dismissed
     * @param key A dynamic key string (e.g., 'resaleDocsWelcome_12345')
     */
    public setDismissedDynamic(key: string): void {
        const storageKey = `${this.DISMISSED_PREFIX}${key}`;
        LocalService.set(storageKey, 'true');
    }
}
